import { useForm } from '@tanstack/react-form';
import { z } from 'zod';

import type { ListB2BPatients } from '@virtual-care/client';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@virtual-care/shadcn';
import { FormMessage, errorToFormIssue, useAction } from '@virtual-care/ui';

type B2BPatientRecord = ListB2BPatients['records'][number];

const schema = z.object({
  usageLimit: z.number().int().min(0),
  printingUsageLimit: z.number().int().min(0),
});

export function EditB2BPatient({
  patient,
  open,
  onOpenChange,
  onSaved,
}: {
  patient: B2BPatientRecord;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved?: () => void;
}) {
  const updateQuotas = useAction('PATCH /b2b/patients/{id}', {
    invalidate: ['GET /b2b/patients'],
  });

  const form = useForm({
    defaultValues: {
      usageLimit: patient.profile.usageLimit,
      printingUsageLimit: patient.profile.printingUsageLimit ?? 0,
    },
    validators: {
      onSubmit: schema,
      onSubmitAsync: async ({ value }) => {
        try {
          await updateQuotas.mutateAsync({
            id: patient.id,
            usageLimit: value.usageLimit,
            printingUsageLimit: value.printingUsageLimit,
          });
          onOpenChange(false);
          onSaved?.();
          return void 0;
        } catch (error) {
          return errorToFormIssue(error);
        }
      },
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit B2B Patient — {patient.profile.name}</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
          className="space-y-4"
        >
          <form.Field name="usageLimit">
            {(field) => (
              <div className="space-y-2">
                <Label htmlFor={field.name}>Consultation usage limit</Label>
                <Input
                  id={field.name}
                  type="number"
                  min={0}
                  value={field.state.value}
                  onChange={(e) =>
                    field.handleChange(parseInt(e.target.value, 10) || 0)
                  }
                />
              </div>
            )}
          </form.Field>

          <form.Field name="printingUsageLimit">
            {(field) => (
              <div className="space-y-2">
                <Label htmlFor={field.name}>3D Printing usage limit</Label>
                <Input
                  id={field.name}
                  type="number"
                  min={0}
                  value={field.state.value}
                  onChange={(e) =>
                    field.handleChange(parseInt(e.target.value, 10) || 0)
                  }
                />
              </div>
            )}
          </form.Field>

          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
            children={([canSubmit, isSubmitting]) => (
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={!canSubmit || isSubmitting}>
                  {isSubmitting ? 'Saving...' : 'Save Changes'}
                </Button>
              </DialogFooter>
            )}
          />

          <FormMessage errors={form.state.errors} />
        </form>
      </DialogContent>
    </Dialog>
  );
}
